import { Component, OnInit, OnDestroy } from '@angular/core';
import { Observable } from 'rxjs';
import { HomeService } from 'src/services/home.service';
import { DatalayerService } from 'src/services/datalayer.service';
import { NavigationEnd, Router } from '@angular/router';
import { ReplaySubject } from 'rxjs';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';
import { MaintenanceMessage } from './cps-home-maintenance-message.interface';

@Component({
  selector: 'app-cps-home-alerts',
  templateUrl: './cps-home-alerts.component.html',
  styleUrls: ['./cps-home-alerts.component.scss']
})
export class CpsHomeAlertsComponent implements OnInit, OnDestroy {

  homeAlerts$: Observable<Element[]>;
  public isClosed: boolean;
  public isHome: boolean;
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  private readonly dismissedMaintenanceMessageKey = 'dismissedMaintenanceMessage';

  public maintenanceMessage: MaintenanceMessage | null = null;
  public isMaintenanceClosed = true;
  private isMaintenanceMessageLoading = false;

  constructor(private datalayerService: DatalayerService,
              private homeService: HomeService,
              private router: Router) {
    this.isClosed = false;
    this.isHome = false;

    let previousUrl: string = '';
    let temporaryUrl: string | null = null;

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        // The component may request the message before login, while no token exists. Retry once the user leaves the login flow and enters the application.
        const shouldRetryMaintenanceMessage = !this.maintenanceMessage && (previousUrl === '' || previousUrl.startsWith('/login')) && !event.url.startsWith('/login');

        // Trigger the bannerImpressionsEvent when the yellow banner alert is displayed on the homepage.
        // In the homepage, this event is triggered after the user logs in using credentials, a refresh token, or a URL with the 'p' parameter.
        // The temporaryUrl and previousUrl variables check for a temporary '/' URL, which occurs when switching between different sections (buckets).
        if (event.url === "/" && previousUrl && temporaryUrl === null) {
            if(previousUrl !== "/login" && previousUrl !== "/home") { // This is added to prevent temporary URL taking the value '/', for the use cases, when user is naviagting from login page to home page directly or through package selection page
              temporaryUrl = event.url;
            }
        } else if (temporaryUrl === "/" && event.url !== "/") {
          temporaryUrl = null;
        }

        if (event.url === "/" && (previousUrl === "" || "/login" || "/home") && temporaryUrl === null) { // This is added for home banner to be visible for the usecases, when user is naviagting from login page to home page directly or through package selection page
          this.isHome = true;
          this.datalayerService.bannerImpressionsEvent('homepage_messages', 'homepage_messages');

        } else if ((event.url.startsWith('/home') || event.url.startsWith('/?p=')) && previousUrl !== event.url) {
          this.isHome = true;
          this.datalayerService.bannerImpressionsEvent('homepage_messages', 'homepage_messages');
        
        } else {
          this.isHome = false;
        }

        if (shouldRetryMaintenanceMessage) {
          this.loadMaintenanceMessage();
        }

        previousUrl = event.url;
      }
    });
  }

  ngOnInit(): void {
    this.homeAlerts$ = this.homeService.loadHomeAlerts();
    this.loadMaintenanceMessage();
  }

  // Loads the maintenance message and determines whether the banner should be displayed.
  private loadMaintenanceMessage(): void {
    // Prevent the initial load and the post-login retry from creating duplicate requests.
    if (this.isMaintenanceMessageLoading) {
      return;
    }

    this.isMaintenanceMessageLoading = true;
    this.homeService.getMaintenanceMessage()
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        maintenanceMessage => {
          this.isMaintenanceMessageLoading = false;
          this.maintenanceMessage = maintenanceMessage;

          const dismissedMessage = localStorage.getItem(this.dismissedMaintenanceMessageKey);
          const wasDismissed = dismissedMessage === maintenanceMessage.title;
          // Hide a message that was dismissed previously or has passed its end date.
          this.isMaintenanceClosed = wasDismissed || !this.isMaintenanceMessageActive(maintenanceMessage);
        },
        () => {
          this.isMaintenanceMessageLoading = false;
          this.maintenanceMessage = null;
          this.isMaintenanceClosed = true;
        }
      );
  }

  closePanel(): void {
    this.isClosed = true;
    this.datalayerService.bannerCloseEvent('homepage_messages', 'homepage_messages');
  }
  
  clickEvent(linkUrl: string): void {
    let extractLinkResult = this.extractLink(linkUrl);
    this.datalayerService.bannerClickEvent('homepage_messages', 'homepage_messages', extractLinkResult);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  extractLink(innerHTML: string): string | null {
    const parser = new DOMParser();
    const doc = parser.parseFromString(innerHTML, 'text/html');
    const anchorElement = doc.querySelector('a');
    const hrefValue = anchorElement?.getAttribute('href');
    return hrefValue;
  }

  // Closes the banner and remembers the dismissed message across page refreshes.
  closeMaintenanceMessage(): void {
    if (!this.maintenanceMessage) {
      return;
    }
    // Persist the title so the same announcement stays closed after a refresh.
    localStorage.setItem(this.dismissedMaintenanceMessageKey, this.maintenanceMessage.title);
    this.isMaintenanceClosed = true;
  }

  // Returns true when the expiration date is valid and the maintenance message has not expired.
  private isMaintenanceMessageActive(maintenanceMessage: MaintenanceMessage): boolean {
    const duration = maintenanceMessage.duration.trim();
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(duration);

    const expirationDate = isDateOnly ? new Date(`${duration}T23:59:59`) : new Date(duration);

    const isValidDate = !isNaN(expirationDate.getTime());
    const hasNotExpired = new Date() <= expirationDate;

    return isValidDate && hasNotExpired;
  }

}
